import { useEffect, useState } from 'react';
import { useAuth } from '@/components/auth/AuthContext';
import { serviceSupabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Search, 
  Loader2, 
  Shield, 
  ShieldX, 
  Trash2, 
  Mail, 
  AlertTriangle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

interface User {
  id: string;
  display_name: string;
  first_name: string;
  last_name: string;
  email: string;
  is_admin: boolean;
  created_at: string;
  last_sign_in_at: string | null;
  provider: string;
  organization_id: string | null;
  organization_name: string | null;
  hubspot_contact_id: string | null;
}

const UserManagement = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [updatingUser, setUpdatingUser] = useState<string | null>(null);
  const [deletingUser, setDeletingUser] = useState<string | null>(null);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [showDebug, setShowDebug] = useState<boolean>(false);
  const usersPerPage = 10;

  // Fetch users with pagination
  useEffect(() => {
    fetchUsers();
  }, [currentPage, searchTerm]);

  const fetchUsers = async () => {
    try {
      setLoading(true);

      // Get users from auth.users via admin API - get all users instead of paginated to allow client-side filtering
      const { data: authUsers, error: authError } = await serviceSupabase.auth.admin
        .listUsers({ 
          perPage: 1000, // Get a large number of users to handle client-side filtering and pagination
        });
      
      if (authError) {
        console.error('Error fetching auth users:', authError);
        throw authError;
      }
      
      // Get total user count
      let filteredUsers = authUsers?.users || [];
      let totalCount = authUsers?.total || 0;
      
      // For each auth user, get their profile from the profiles table
      const userIds = filteredUsers.map((u: any) => u.id) || [];
      let profilesData: any[] = [];
      
      if (userIds.length > 0) {
        const { data: profiles, error: profilesError } = await serviceSupabase
          .from('profiles')
          .select('id, first_name, last_name, is_admin, organization_id, hubspot_contact_id')
          .in('id', userIds);
          
        if (profilesError) {
          console.error('Error fetching profiles:', profilesError);
          throw profilesError;
        }
        
        profilesData = profiles || [];
      }

      // Get organization information
      const organizationIds = profilesData
        .filter(profile => profile.organization_id)
        .map(profile => profile.organization_id);
      
      let organizationsData: any[] = [];
      
      if (organizationIds.length > 0) {
        const { data: organizations, error: orgsError } = await serviceSupabase
          .from('organizations')
          .select('id, name')
          .in('id', organizationIds);
          
        if (orgsError) {
          console.error('Error fetching organizations:', orgsError);
          throw orgsError;
        }
        
        organizationsData = organizations || [];
      }
      
      // Transform data to combine auth user, profile data, and organization data
      let transformedData = filteredUsers.map((authUser: any) => {
        const profile = profilesData.find(p => p.id === authUser.id) || {};
        const organization = organizationsData.find(org => org.id === profile.organization_id) || {};
        
        // Determine auth provider
        let provider = 'Email';
        if (authUser.app_metadata?.provider) {
          provider = authUser.app_metadata.provider.charAt(0).toUpperCase() + 
                    authUser.app_metadata.provider.slice(1);
        } else if (authUser.email && authUser.email.includes('@gmail.com')) {
          provider = 'Google';
        }
        
        return {
          id: authUser.id,
          display_name: profile.first_name && profile.last_name 
            ? `${profile.first_name} ${profile.last_name}` 
            : authUser.email?.split('@')[0] || `User ${authUser.id.substring(0, 8)}`,
          first_name: profile.first_name || '',
          last_name: profile.last_name || '',
          email: authUser.email || '',
          is_admin: profile.is_admin || false,
          created_at: authUser.created_at,
          last_sign_in_at: authUser.last_sign_in_at || null,
          provider,
          organization_id: profile.organization_id || null,
          organization_name: organization.name || null,
          hubspot_contact_id: profile.hubspot_contact_id || null
        };
      }) || [];

      // Filter data based on search term
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        transformedData = transformedData.filter((user: User) => 
          user.display_name.toLowerCase().includes(searchLower) ||
          user.email.toLowerCase().includes(searchLower) ||
          user.id.toLowerCase().includes(searchLower) ||
          (user.organization_name && user.organization_name.toLowerCase().includes(searchLower))
        );
      }
      
      // Update total count based on filtered data
      totalCount = transformedData.length;
      
      // Calculate total pages
      const calculatedTotalPages = Math.max(1, Math.ceil(totalCount / usersPerPage));
      
      // Reset to page 1 if current page is greater than total pages
      if (currentPage > calculatedTotalPages && calculatedTotalPages > 0) {
        setCurrentPage(1);
        // Since we're not using useEffect dependency, we need to handle this manually
        const startIndex = 0;
        const paginatedData = transformedData.slice(startIndex, startIndex + usersPerPage);
        
        setTotalPages(calculatedTotalPages);
        setTotalUsers(totalCount);
        setUsers(paginatedData);
        setLoading(false);
        return;
      }
      
      // Apply pagination to the filtered data
      const startIndex = (currentPage - 1) * usersPerPage;
      const paginatedData = transformedData.slice(startIndex, startIndex + usersPerPage);
      
      console.log('Pagination info:', {
        totalCount,
        filteredCount: transformedData.length,
        usersPerPage,
        calculatedTotalPages,
        currentPage,
        startIndex,
        showing: `${startIndex+1}-${startIndex + paginatedData.length} of ${totalCount}`,
        searchTerm: searchTerm || 'none'
      });
      
      setTotalPages(calculatedTotalPages);
      setTotalUsers(totalCount);
      setUsers(paginatedData);
      
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const toggleAdminAccess = async (userId: string, currentStatus: boolean) => {
    try {
      setUpdatingUser(userId);
      
      // Update the profile
      const { error: updateError } = await serviceSupabase
        .from('profiles')
        .update({ 
          is_admin: !currentStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (updateError) {
        console.error('Update error:', updateError);
        throw updateError;
      }

      // Update local state
      setUsers(users.map(u => 
        u.id === userId 
          ? { ...u, is_admin: !currentStatus }
          : u
      ));

      toast.success(`Successfully ${!currentStatus ? 'granted' : 'removed'} admin access`);
    } catch (error) {
      console.error('Error updating admin access:', error);
      toast.error('Failed to update admin access');
    } finally {
      setUpdatingUser(null);
    }
  };

  const confirmDeleteUser = (user: User) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const deleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      setDeletingUser(userToDelete.id);
      
      // Step 1: Check if user owns organizations
      const { data: orgsData, error: orgsError } = await serviceSupabase
        .from('organizations')
        .select('id')
        .eq('owner_id', userToDelete.id);
        
      if (orgsError) {
        console.error('Error checking organizations:', orgsError);
        throw orgsError;
      }
      
      // Step 2: Handle organizations if they exist
      if (orgsData && orgsData.length > 0) {
        for (const org of orgsData) {
          // Handle assessments
          const { data: assessmentIds } = await serviceSupabase
            .from('assessments')
            .select('id')
            .eq('organization_id', org.id);
            
          if (assessmentIds && assessmentIds.length > 0) {
            const ids = assessmentIds.map((a: any) => a.id);
            
            // Delete GPTZero results first
            await serviceSupabase
              .from('gptzero_results')
              .delete()
              .in('assessment_id', ids);
              
            // Delete candidate_assessments
            await serviceSupabase
              .from('candidate_assessments')
              .delete()
              .in('assessment_id', ids);
          }
          
          // Delete assessments for this organization
          await serviceSupabase
            .from('assessments')
            .delete()
            .eq('organization_id', org.id);
            
          // Update profiles
          await serviceSupabase
            .from('profiles')
            .update({ organization_id: null })
            .eq('organization_id', org.id);
            
          // Delete credits
          await serviceSupabase
            .from('credits')
            .delete()
            .eq('organization_id', org.id);
            
          // Delete the organization
          await serviceSupabase
            .from('organizations')
            .delete()
            .eq('id', org.id);
        }
      }
      
      // Step 3: Handle user assessments
      const { data: userAssessmentIds } = await serviceSupabase
        .from('assessments')
        .select('id')
        .eq('user_id', userToDelete.id);
        
      if (userAssessmentIds && userAssessmentIds.length > 0) {
        const ids = userAssessmentIds.map((a: any) => a.id);
        
        // Delete GPTZero results first
        await serviceSupabase
          .from('gptzero_results')
          .delete()
          .in('assessment_id', ids);
          
        // Delete candidate_assessments
        await serviceSupabase
          .from('candidate_assessments')
          .delete()
          .in('assessment_id', ids);
          
        // Delete assessments
        await serviceSupabase
          .from('assessments')
          .delete()
          .eq('user_id', userToDelete.id);
      }
      
      // Step 4: Delete profile
      await serviceSupabase
        .from('profiles')
        .delete()
        .eq('id', userToDelete.id);
        
      // Step 5: Delete auth user
      const { error: deleteError } = await serviceSupabase.auth.admin.deleteUser(
        userToDelete.id
      );
      
      if (deleteError) {
        throw deleteError;
      }
      
      // Update UI
      setUsers(users.filter(u => u.id !== userToDelete.id));
      toast.success(`Successfully deleted user ${userToDelete.display_name}`);
      setDeleteDialogOpen(false);
      fetchUsers(); // Refresh the list
      
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user. Please try again.');
    } finally {
      setDeletingUser(null);
      setUserToDelete(null);
    }
  };

  // Handle page changes
  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Format date for display
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }) + ', ' + date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Users</CardTitle>
        <CardDescription>Manage users and their permissions</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <div className="flex justify-between items-center mb-4">
            <div className="relative w-72">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, email or user ID..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1); // Reset to first page on new search
                }}
                className="pl-10"
              />
            </div>
            <div className="text-sm text-muted-foreground">
              {searchTerm ? `${totalUsers} results found` : `Total: ${totalUsers} users`}
            </div>
          </div>

          {loading ? (
            <div className="p-8 flex justify-center">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : (
            <div>
              <Table className="border-collapse">
                <TableHeader>
                  <TableRow className="border-b border-border bg-muted/50">
                    <TableHead className="h-10 px-4 text-left text-xs font-medium text-muted-foreground">Display Name</TableHead>
                    <TableHead className="h-10 px-4 text-left text-xs font-medium text-muted-foreground">Email</TableHead>
                    <TableHead className="h-10 px-4 text-left text-xs font-medium text-muted-foreground">Phone</TableHead>
                    <TableHead className="h-10 px-4 text-left text-xs font-medium text-muted-foreground">Organization</TableHead>
                    <TableHead className="h-10 px-4 text-left text-xs font-medium text-muted-foreground">Provider</TableHead>
                    <TableHead className="h-10 px-4 text-left text-xs font-medium text-muted-foreground">Created At</TableHead>
                    <TableHead className="h-10 px-4 text-left text-xs font-medium text-muted-foreground">Last Sign In</TableHead>
                    <TableHead className="h-10 px-4 text-left text-xs font-medium text-muted-foreground">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.length > 0 ? (
                    users.map((user, index) => (
                      <TableRow 
                        key={user.id}
                        className={index % 2 === 0 ? "bg-background" : "bg-muted/20"}
                      >
                        <TableCell className="p-4 text-sm">{user.display_name}</TableCell>
                        <TableCell className="p-4 text-sm">{user.email}</TableCell>
                        <TableCell className="p-4 text-sm">-</TableCell>
                        <TableCell className="p-4 text-sm">{user.organization_name || '-'}</TableCell>
                        <TableCell className="p-4 text-sm">
                          {user.provider === 'Google' ? (
                            <div className="flex items-center">
                              <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
                                <path
                                  fill="#4285F4"
                                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                />
                                <path
                                  fill="#34A853"
                                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                />
                                <path
                                  fill="#FBBC05"
                                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                />
                                <path
                                  fill="#EA4335"
                                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                />
                              </svg>
                              Google
                            </div>
                          ) : user.provider === 'Email' ? (
                            <Mail className="h-4 w-4" />
                          ) : (
                            user.provider
                          )}
                        </TableCell>
                        <TableCell className="p-4 text-sm">{formatDate(user.created_at)}</TableCell>
                        <TableCell className="p-4 text-sm">{formatDate(user.last_sign_in_at)}</TableCell>
                        <TableCell className="p-4 text-sm">
                          <div className="flex items-center gap-2">
                            {user.hubspot_contact_id && (
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8 rounded-full p-0 hover:bg-orange-50 border-orange-200"
                                onClick={() => window.open(`https://app-eu1.hubspot.com/contacts/144231351/record/0-1/${user.hubspot_contact_id}`, '_blank')}
                                title="View in HubSpot"
                              >
                                <img 
                                  src="/hubspot-svgrepo-com.svg" 
                                  alt="HubSpot" 
                                  className="h-4 w-4"
                                />
                              </Button>
                            )}
                            <Button
                              variant="secondary"
                              size="icon"
                              className="h-8 w-8 rounded-full bg-muted p-0 hover:bg-muted-foreground/10"
                              onClick={() => toggleAdminAccess(user.id, user.is_admin)}
                              disabled={updatingUser === user.id}
                            >
                              {updatingUser === user.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : user.is_admin ? (
                                <ShieldX className="h-4 w-4" />
                              ) : (
                                <Shield className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              variant="destructive"
                              size="icon"
                              className="h-8 w-8 rounded-full p-0"
                              onClick={() => confirmDeleteUser(user)}
                              disabled={deletingUser === user.id}
                            >
                              {deletingUser === user.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} className="h-40 text-center">
                        No users found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {/* Debug section */}
              <div className="mt-4 mb-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowDebug(!showDebug)}
                  className="text-xs"
                >
                  {showDebug ? "Hide Debug" : "Show Debug"}
                </Button>
                
                {showDebug && (
                  <div className="mt-2 p-3 bg-muted rounded text-xs">
                    <p>Current Page: {currentPage}</p>
                    <p>Total Pages: {totalPages}</p>
                    <p>Total Users: {totalUsers}</p>
                    <p>Users Per Page: {usersPerPage}</p>
                    <p>Users in Current Page: {users.length}</p>
                    <div className="mt-2 flex gap-1">
                      {Array.from({length: totalPages}, (_, i) => (
                        <Button 
                          key={i+1} 
                          variant={currentPage === i+1 ? "default" : "outline"} 
                          size="sm"
                          className={`w-8 h-8 p-0 text-xs ${
                            currentPage === i+1 
                              ? 'bg-black text-white font-medium border-0' 
                              : 'border-muted bg-transparent text-gray-500'
                          }`}
                          onClick={() => goToPage(i+1)}
                        >
                          {i+1}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {totalPages > 1 && (
                <div className="mt-6 flex justify-center">
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8 rounded-md border-muted p-0 bg-transparent text-gray-500"
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      // For pages <= 5, show all pages 1 through 5
                      // For pages > 5, show 1, ... , current-1, current, current+1, ... , totalPages
                      let pageNumber;
                      if (totalPages <= 5) {
                        pageNumber = i + 1;
                      } else if (i === 0) {
                        pageNumber = 1;
                      } else if (i === 1 && currentPage > 3) {
                        return (
                          <div key="ellipsis-start" className="flex h-8 w-8 items-center justify-center">
                            <span>...</span>
                          </div>
                        );
                      } else if (i === 3 && currentPage < totalPages - 2) {
                        return (
                          <div key="ellipsis-end" className="flex h-8 w-8 items-center justify-center">
                            <span>...</span>
                          </div>
                        );
                      } else if (i === 4) {
                        pageNumber = totalPages;
                      } else {
                        const middlePage = Math.min(Math.max(currentPage, 3), totalPages - 2);
                        pageNumber = middlePage + (i - 2);
                      }
                      
                      const isActive = currentPage === pageNumber;
                      
                      return (
                        <Button
                          key={pageNumber}
                          variant={isActive ? "default" : "outline"}
                          size="icon"
                          className={`h-8 w-8 rounded-md p-0 ${
                            isActive 
                              ? 'bg-black text-white font-medium border-0' 
                              : 'border-muted bg-transparent text-gray-500'
                          }`}
                          onClick={() => goToPage(pageNumber)}
                        >
                          {pageNumber}
                        </Button>
                      );
                    })}
                    
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8 rounded-md border-muted p-0 bg-transparent text-gray-500"
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Confirm User Deletion
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete user "{userToDelete?.display_name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              This will delete the user and all their data, including:
            </p>
            <ul className="list-disc list-inside mt-2 text-sm text-muted-foreground">
              <li>Organizations they own</li>
              <li>Assessments they've created</li>
              <li>All profile information</li>
            </ul>
            {userToDelete?.email && (
              <div className="mt-4 p-2 bg-muted rounded-md">
                <p className="text-sm flex items-center">
                  <Mail className="h-4 w-4 mr-2" />
                  Email: <span className="font-medium ml-1">{userToDelete.email}</span>
                </p>
                <p className="text-sm mt-1">
                  ID: <span className="font-mono">{userToDelete.id}</span>
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deletingUser !== null}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={deleteUser}
              disabled={deletingUser !== null}
            >
              {deletingUser ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete User"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default UserManagement; 